import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/auth-store';

export function CompanySwitcher() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { getCurrentTenant, getConnectedTenants } = useAuthStore();

  const currentTenant = getCurrentTenant();
  const connectedTenants = getConnectedTenants();

  // Get company name from companyInfo if available, fall back to tenant.name
  const getCompanyName = (tenant: typeof currentTenant) => {
    if (!tenant) return '';
    return (
      (tenant as { companyInfo?: { companyName?: string } })?.companyInfo?.companyName ||
      tenant.name
    );
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Don't render if no tenant or only one tenant
  if (!currentTenant || connectedTenants.length <= 1) {
    // Just show the company name without dropdown
    const companyName = getCompanyName(currentTenant);
    if (!companyName) return null;

    return <span className="text-sm text-gray-500">{companyName}</span>;
  }

  const handleSelectTenant = (tenantId: string) => {
    setIsOpen(false);
    // Navigate to loading screen which will validate the connection
    navigate(`/tenant/${tenantId}/loading`);
  };

  const handleManageCompanies = () => {
    setIsOpen(false);
    navigate('/companies');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
      >
        <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center">
          <span className="text-primary-600 font-semibold text-xs">
            {getCompanyName(currentTenant).charAt(0).toUpperCase()}
          </span>
        </div>
        <span className="font-medium">{getCompanyName(currentTenant)}</span>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute left-0 mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
          <div className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide border-b border-gray-100">
            Switch Company
          </div>
          {connectedTenants.map((tenant) => {
            const isSelected = tenant.id === currentTenant?.id;
            return (
              <button
                key={tenant.id}
                onClick={() => handleSelectTenant(tenant.id)}
                className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 transition-colors ${
                  isSelected ? 'bg-primary-50' : ''
                }`}
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    isSelected ? 'bg-primary-100' : 'bg-gray-100'
                  }`}
                >
                  <span
                    className={`font-semibold text-sm ${
                      isSelected ? 'text-primary-600' : 'text-gray-600'
                    }`}
                  >
                    {getCompanyName(tenant).charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div
                    className={`font-medium truncate ${
                      isSelected ? 'text-primary-700' : 'text-gray-900'
                    }`}
                  >
                    {getCompanyName(tenant)}
                  </div>
                  <div className="text-xs text-gray-500">QuickBooks connected</div>
                </div>
                {isSelected && (
                  <svg
                    className="w-5 h-5 text-primary-500 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
            );
          })}
          <div className="border-t border-gray-100 mt-1 pt-1">
            <button
              onClick={handleManageCompanies}
              className="w-full flex items-center gap-3 px-3 py-2 text-left text-sm text-gray-600 hover:bg-gray-50 transition-colors"
            >
              <svg
                className="w-5 h-5 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              Manage Companies
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
